'use client';

/**
 * Modal — canonical dialog primitive (#109).
 *
 * Mirrors design/primitives.jsx (the spec's compound Modal API). Replaces
 * the 5+ ad-hoc dialog implementations in src/components/recipe/ (each
 * with its own focus trap, scroll lock, and scrim styling).
 *
 * Compound shape:
 *
 *   <Modal open={open} onClose={close} size="md" labelledBy="confirm-title">
 *     <ModalHeader id="confirm-title" onClose={close}>Remove recipe?</ModalHeader>
 *     <ModalBody>This will remove the recipe from your cookbook…</ModalBody>
 *     <ModalFooter>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button variant="destructive-soft" onClick={confirm}>Remove</Button>
 *     </ModalFooter>
 *   </Modal>
 *
 * Built on the existing useFocusTrap hook (Esc closes, Tab cycles within
 * the dialog, focus restores to the trigger on unmount per WCAG 2.4.3).
 * Adds: scroll lock on body, scrim click closes, mount/unmount with
 * enter/exit transitions, an `inline` mode for gallery-style static
 * rendering.
 */

import { useEffect, useReducer, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from './icon';
import { useFocusTrap } from './use-focus-trap';
import styles from './modal.module.css';

type ModalPhase = 'closed' | 'entering' | 'entered' | 'exiting';
type PhaseAction = 'OPEN' | 'OPENED' | 'CLOSE' | 'CLOSED';

function phaseReducer(state: ModalPhase, action: PhaseAction): ModalPhase {
  switch (action) {
    case 'OPEN':   return state === 'entered' ? 'entered' : 'entering';
    case 'OPENED': return 'entered';
    case 'CLOSE':  return state === 'closed' ? 'closed' : 'exiting';
    case 'CLOSED': return 'closed';
  }
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'full';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: ModalSize;
  labelledBy?: string;
  describedBy?: string;
  /**
   * Gallery / spec-rendering mode. Skips scroll lock + focus trap +
   * enter/exit transitions; renders the dialog inline at the call site
   * instead of fixed to the viewport. Use only when you want to show a
   * modal "frozen open" (e.g. design spec previews).
   */
  inline?: boolean;
  closeOnScrim?: boolean;
  closeOnEsc?: boolean;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  size = 'md',
  labelledBy,
  describedBy,
  inline = false,
  closeOnScrim = true,
  closeOnEsc = true,
  children,
}: ModalProps) {
  // Phase machine for mount/unmount with enter/exit transitions. A reducer
  // keeps the effect's deps tight (`open`, `inline` only) — without it, a
  // `mounted` state in the deps array would cascade-rerender every time the
  // effect set it. Phase derives mounted + entered.
  const [phase, dispatch] = useReducer(
    phaseReducer,
    open && !inline ? 'entered' : 'closed',
  );
  const mounted = phase !== 'closed' || inline;
  const entered = phase === 'entering' || phase === 'entered';

  useEffect(() => {
    if (inline) {
      dispatch('OPENED');
      return;
    }
    if (open) {
      dispatch('OPEN');
      // Two frames so the enter transition has a frame to interpolate.
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => dispatch('OPENED'));
        return () => cancelAnimationFrame(id2);
      });
      return () => cancelAnimationFrame(id1);
    }
    dispatch('CLOSE');
    // Match the longest --motion-overlay-exit duration (--t-base = 180ms).
    const t = setTimeout(() => dispatch('CLOSED'), 200);
    return () => clearTimeout(t);
  }, [open, inline]);

  // Scroll lock on body while a fixed (non-inline) modal is mounted.
  useEffect(() => {
    if (inline || !mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted, inline]);

  // Focus trap + Esc handling via the existing hook. Disabled in inline
  // mode (spec-gallery use — the dialog isn't actually interactive).
  const trapOnClose = closeOnEsc ? onClose : () => {};
  const trapRef = useFocusTrap(trapOnClose);

  if (!mounted) return null;

  const handleScrim = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnScrim && e.target === e.currentTarget && !inline) onClose();
  };

  const rootClassName = [
    styles.root,
    inline ? styles.rootInline : '',
    entered ? styles.isOpen : '',
    styles[`root-${size}`] ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} onMouseDown={handleScrim}>
      <div className={styles.scrim} aria-hidden="true" />
      <div
        ref={inline ? undefined : trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className={`${styles.modal} ${styles[`modal-${size}`]}`}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}

interface ModalHeaderProps {
  id?: string;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
}

export function ModalHeader({ id, onClose, children, className }: ModalHeaderProps) {
  const tc = useTranslations('common');
  return (
    <header className={[styles.header, className].filter(Boolean).join(' ')}>
      <h2 id={id} className={styles.title}>
        {children}
      </h2>
      {onClose ? (
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={tc('closeDialog')}
        >
          <Icon name="x" size={18} />
        </button>
      ) : null}
    </header>
  );
}

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export function ModalBody({ children, className }: ModalBodyProps) {
  return <div className={[styles.body, className].filter(Boolean).join(' ')}>{children}</div>;
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <footer className={[styles.footer, className].filter(Boolean).join(' ')}>{children}</footer>
  );
}
